// Playbook Library hooks — FE-044.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  applyPlaybook,
  archivePlaybook,
  createPlaybook,
  deletePlaybook,
  duplicatePlaybook,
  fetchPlaybookApplications,
  fetchPlaybookDetail,
  fetchPlaybookList,
  refreshPlaybookBlocks,
  restorePlaybook,
  revertPlaybookApplication,
  updatePlaybook,
  type ApplyPlaybookInput,
  type ApplyPlaybookResponse,
  type PlaybookApplicationsListResponse,
  type RevertPlaybookApplicationResponse,
} from "./api";
import type {
  CreatePlaybookInput,
  PlaybookBlocksRefreshResponse,
  PlaybookListResponse,
  PlaybookListRow,
  PlaybookStatusFilter,
} from "./types";


export const PLAYBOOKS_QUERY_KEY = "playbooks" as const;


export function usePlaybookListQuery(
  status: PlaybookStatusFilter,
): UseQueryResult<PlaybookListResponse> {
  return useQuery({
    queryKey: [PLAYBOOKS_QUERY_KEY, "list", status],
    queryFn: ({ signal }) => fetchPlaybookList(status, { signal }),
  });
}


export function usePlaybookDetailQuery(
  playbookId: string,
): UseQueryResult<PlaybookListRow> {
  return useQuery({
    queryKey: [PLAYBOOKS_QUERY_KEY, "detail", playbookId],
    queryFn: ({ signal }) => fetchPlaybookDetail(playbookId, { signal }),
    enabled: playbookId.length > 0,
  });
}


function invalidateAllListQueries(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({
    queryKey: [PLAYBOOKS_QUERY_KEY, "list"],
  });
}


export function useCreatePlaybookMutation(): UseMutationResult<
  PlaybookListRow, Error, CreatePlaybookInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePlaybookInput) => createPlaybook(input),
    onSuccess: () => invalidateAllListQueries(qc),
  });
}


export function useDuplicatePlaybookMutation(): UseMutationResult<
  PlaybookListRow, Error, string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (playbookId: string) => duplicatePlaybook(playbookId),
    onSuccess: () => invalidateAllListQueries(qc),
  });
}


export function useUpdatePlaybookMutation(): UseMutationResult<
  PlaybookListRow, Error, { playbookId: string; name: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ playbookId, name }) => updatePlaybook(playbookId, { name }),
    onSuccess: (row) => {
      qc.invalidateQueries({
        queryKey: [PLAYBOOKS_QUERY_KEY, "detail", row.playbook_id],
      });
      invalidateAllListQueries(qc);
    },
  });
}


export function useArchivePlaybookMutation(): UseMutationResult<
  PlaybookListRow, Error, string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (playbookId: string) => archivePlaybook(playbookId),
    onSuccess: () => invalidateAllListQueries(qc),
  });
}


export function useRestorePlaybookMutation(): UseMutationResult<
  PlaybookListRow, Error, string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (playbookId: string) => restorePlaybook(playbookId),
    onSuccess: () => invalidateAllListQueries(qc),
  });
}


export function useDeletePlaybookMutation(): UseMutationResult<
  void, Error, string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (playbookId: string) => deletePlaybook(playbookId),
    onSuccess: () => invalidateAllListQueries(qc),
  });
}


export function useRefreshPlaybookBlocksMutation(): UseMutationResult<
  PlaybookBlocksRefreshResponse, Error,
  { playbookId: string; blocksJson: unknown }
> {
  return useMutation({
    mutationFn: ({ playbookId, blocksJson }) =>
      refreshPlaybookBlocks(playbookId, blocksJson),
  });
}


// ── BE-046 — Apply / revert mutations ────────────────────────────────────


export function useApplyPlaybookMutation(): UseMutationResult<
  ApplyPlaybookResponse,
  Error,
  { playbookId: string; input: ApplyPlaybookInput }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ playbookId, input }) =>
      applyPlaybook(playbookId, input),
    onSuccess: (response) => {
      // Only refetch the Commitment + Playbook lists when the call
      // actually mutated state. The duplicate-warning + dry-run
      // branches return early without spawning Commitments.
      if (response.requires_confirmation) return;
      if (response.dry_run) return;
      qc.invalidateQueries({ queryKey: ["commitments"] });
      invalidateAllListQueries(qc);
    },
  });
}


export function useRevertPlaybookApplicationMutation(): UseMutationResult<
  RevertPlaybookApplicationResponse, Error, string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (applicationId: string) =>
      revertPlaybookApplication(applicationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["commitments"] });
      qc.invalidateQueries({
        queryKey: [PLAYBOOKS_QUERY_KEY, "applications"],
      });
      invalidateAllListQueries(qc);
    },
  });
}


// ── FE-047 — recent-applications query ───────────────────────────────────


export function usePlaybookApplicationsQuery(
  playbookId: string,
  enabled: boolean = true,
): UseQueryResult<PlaybookApplicationsListResponse> {
  return useQuery({
    queryKey: [PLAYBOOKS_QUERY_KEY, "applications", playbookId],
    queryFn: ({ signal }) =>
      fetchPlaybookApplications(playbookId, { signal }),
    enabled: enabled && playbookId.length > 0,
  });
}
